/**
 * Small shared components. They render the original app's markup and class names, so app.css styles them unchanged.
 */
import { useId, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { MetricKind, Project } from '@/types/models';
import { daysLeft, fmtDate, initials } from '@/domain/format';
import { KINDS } from '@/domain/metrics';
import { deadline, statusClass } from '@/domain/projects';
import { PERIODS, type PeriodKey } from '@/domain/periods';
import { LIMITS } from '@/domain/validation';
import { useModal } from '@/app/AppContext';
import { Icon, type IconName } from './Icon';

/* ---------- page chrome ---------- */

export function Topbar({ title, sub, children }: { title: ReactNode; sub?: ReactNode; children?: ReactNode }) {
  return (
    <div className="topbar">
      <div>
        <h1>{title}</h1>
        {sub ? <div className="sub">{sub}</div> : null}
      </div>
      <div className="row">{children}</div>
    </div>
  );
}

export function StatCard({ n, suffix, label, onClick, to, color }: { n: ReactNode; suffix?: ReactNode; label: ReactNode; onClick?: () => void; to?: string; color?: string }) {
  const body = (
    <>
      <div className="n" style={color ? { color } : undefined}>
        {n}
        {suffix ? (
          <span className="hint" style={{ fontSize: 13, fontWeight: 400 }}>
            {suffix}
          </span>
        ) : null}
      </div>
      <div className="l">{label}</div>
    </>
  );
  if (to)
    return (
      <Link className="card stat" to={to} style={{ color: 'inherit', textDecoration: 'none' }}>
        {body}
      </Link>
    );
  return (
    <div className="card stat" onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined} onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined} style={onClick ? undefined : { cursor: 'default' }}>
      {body}
    </div>
  );
}

export function NoRecords({ cols }: { cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="empty">
        No records found.
      </td>
    </tr>
  );
}

export function IconButton({ icon, tip, onClick, danger, disabled }: { icon: IconName; tip: string; onClick: () => void; danger?: boolean; disabled?: boolean }) {
  return (
    <button type="button" className={'icon-btn' + (danger ? ' danger' : '')} data-tip={tip} aria-label={tip} onClick={onClick} disabled={disabled}>
      <Icon name={icon} />
    </button>
  );
}

export function ActionsCell({ children }: { children: ReactNode }) {
  return (
    <div className="row" style={{ gap: 4, flexWrap: 'nowrap' }}>
      {children}
    </div>
  );
}

export function StatusBadge({ status, style }: { status: string; style?: React.CSSProperties }) {
  return (
    <span className={`badge ${statusClass(status)}`} style={style}>
      {status}
    </span>
  );
}

export function Avatar({ name, size }: { name: string; size?: number }) {
  return (
    <span className="avatar" style={size ? { width: size, height: size, fontSize: 13 } : undefined}>
      {initials(name)}
    </span>
  );
}

export function MemberLink({ id, name }: { id: string; name: string }) {
  return (
    <Link to={`/members/${encodeURIComponent(id)}`} style={{ textDecoration: 'none', fontWeight: 600 }}>
      <Avatar name={name} />
      {name}
    </Link>
  );
}

export function ProjectLinks({ project, empty = '—' }: { project: Pick<Project, 'links'>; empty?: ReactNode }) {
  if (!project.links.length) return <span className="muted">{empty}</span>;
  return (
    <>
      {project.links.map((l, i) => (
        <a key={i} href={l.url} target="_blank" rel="noopener noreferrer">
          {l.label} ↗
        </a>
      ))}
    </>
  );
}

/** Metric type tag — "Type not set" (dashed) when nobody chose one. */
export function KindTag({ kind }: { kind: MetricKind | '' }) {
  if (!kind)
    return (
      <span className="badge hold" title="Nobody has set this metric's type. Set it in Configure metrics." style={{ fontWeight: 500, background: 'none', border: '1px dashed var(--border)' }}>
        Type not set
      </span>
    );
  return (
    <span className={`badge ${kind === 'daily' ? 'progress' : kind === 'todate' ? 'planning' : 'hold'}`} title={KINDS[kind][1]} style={{ fontWeight: 500 }}>
      {KINDS[kind][0]}
    </span>
  );
}

/** "Last project log" style cell: date + today / yesterday / Nd ago (amber badge when `flagAfter` days or more). */
export function AgoCell({ iso, flagAfter }: { iso?: string; flagAfter?: number | null }) {
  if (!iso) return <span className="muted">Never</span>;
  const n = -(daysLeft(iso) ?? 0);
  const t = n === 0 ? 'today' : n === 1 ? 'yesterday' : `${n}d ago`;
  return (
    <>
      <span className="mono">{fmtDate(iso)}</span> {flagAfter != null && n >= flagAfter ? <span className="badge planning">{t}</span> : <span className="hint">{t}</span>}
    </>
  );
}

/** Deadline cell: date, days left / overdue, number of changes. `onSet` opens the deadline modal. */
export function DeadlineCell({ project: p, onSet }: { project: Project; onSet?: () => void }) {
  const d = deadline(p);
  if (!d)
    return onSet ? (
      <a
        href="#"
        className="muted"
        data-tip="Set deadline"
        onClick={(e) => {
          e.preventDefault();
          onSet();
        }}
      >
        Set deadline
      </a>
    ) : (
      <span className="muted">—</span>
    );
  const n = daysLeft(d) ?? 0;
  const ext = (p.extensions || []).length;
  const done = p.status === 'Completed';
  const date = onSet ? (
    <a
      href="#"
      className="mono"
      data-tip="Change deadline (reason required)"
      onClick={(e) => {
        e.preventDefault();
        onSet();
      }}
      style={{ textDecoration: 'none', color: 'inherit', borderBottom: '1px dashed var(--muted)' }}
    >
      {fmtDate(d)}
    </a>
  ) : (
    <span className="mono">{fmtDate(d)}</span>
  );
  return (
    <>
      {date} {done ? null : n < 0 ? <span className="badge delayed">{-n}d overdue</span> : n <= 7 ? <span className="badge planning">{n}d left</span> : <span className="badge progress">{n}d left</span>}
      {ext ? (
        <>
          {' '}
          <span className="hint" title={`Extended ${ext} time(s)`}>
            (+{ext})
          </span>
        </>
      ) : null}
    </>
  );
}

/* ---------- inputs ---------- */

export function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="search">
      <Icon name="search" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} maxLength={LIMITS.search} aria-label={placeholder} />
      <button type="button" className="clear" data-tip="Clear" aria-label="Clear" onClick={() => onChange('')}>
        ✕
      </button>
    </div>
  );
}

export interface PeriodValue {
  key: PeriodKey;
  from: string;
  to: string;
}
export function PeriodSelect({ value, onChange, label = 'Period' }: { value: PeriodValue; onChange: (v: PeriodValue) => void; label?: string }) {
  return (
    <>
      <select style={{ width: 'auto' }} value={value.key} aria-label={label} onChange={(e) => onChange({ ...value, key: e.target.value as PeriodKey })}>
        {PERIODS.map(([k, l]) => (
          <option key={k} value={k}>
            {l}
          </option>
        ))}
      </select>
      {value.key === 'custom' ? (
        <span className="row" style={{ gap: 6 }}>
          <input type="date" value={value.from} onChange={(e) => onChange({ ...value, from: e.target.value })} style={{ width: 'auto' }} aria-label="From" />
          <span className="muted">to</span>
          <input type="date" value={value.to} onChange={(e) => onChange({ ...value, to: e.target.value })} style={{ width: 'auto' }} aria-label="To" />
        </span>
      ) : null}
    </>
  );
}

/** Form field with label, red asterisk for required fields, and the error below the input. */
export function Field({ label, required, error, hint, children, style, id }: { label: ReactNode; required?: boolean; error?: string; hint?: ReactNode; children: ReactNode; style?: React.CSSProperties; id?: string }) {
  return (
    <div className={'field' + (error ? ' invalid' : '')} style={style} id={id}>
      <label className={required ? 'req' : undefined}>{label}</label>
      {children}
      {error ? <div className="err" role="alert">{error}</div> : null}
      {hint ? <div className="hint">{hint}</div> : null}
    </div>
  );
}

/* ---------- modals ---------- */

export function ModalFrame({ title, onClose, children, footer }: { title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  const id = useId();
  return (
    <>
      <div className="modal-h">
        <h3 id={id}>{title}</h3>
        <button type="button" className="icon-btn" onClick={onClose} data-tip="Close" aria-label="Close">
          ✕
        </button>
      </div>
      <div className="modal-b">{children}</div>
      {footer ? <div className="modal-f">{footer}</div> : null}
    </>
  );
}

export interface ConfirmOptions {
  title?: string;
  button?: string;
  /** false → blue button (non-destructive confirm) */
  danger?: boolean;
}
/** Descriptive confirmation modal (never a browser confirm()). Same wording and buttons as the original. */
export function useConfirm() {
  const modal = useModal();
  return (text: string, onYes: () => void | Promise<unknown>, opt: ConfirmOptions = {}) =>
    modal.open((close) => (
      <ModalFrame
        title={opt.title || 'Confirm Delete'}
        onClose={close}
        footer={
          <>
            <button type="button" className="btn" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className={'btn ' + (opt.danger === false ? 'primary' : 'danger')}
              onClick={() => {
                close();
                void onYes();
              }}
            >
              {opt.button || 'Delete'}
            </button>
          </>
        }
      >
        {text}
      </ModalFrame>
    ));
}

/* ---------- states ---------- */

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="panel" aria-busy="true">
      <div className="empty state-loading" role="status">
        <span className="spinner" aria-hidden="true" />
        {label}
      </div>
    </div>
  );
}

export function ErrorState({ error, onRetry, title = 'Could not load data.' }: { error: unknown; onRetry?: () => void; title?: string }) {
  const msg = error instanceof Error ? error.message : String(error || '');
  return (
    <div className="panel">
      <div className="empty state-error" role="alert">
        <div style={{ fontWeight: 600, color: 'var(--danger)' }}>{title}</div>
        {msg ? <div className="hint" style={{ marginTop: 4 }}>{msg}</div> : null}
        {onRetry ? (
          <button type="button" className="btn sm" style={{ marginTop: 12 }} onClick={onRetry}>
            Try again
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function NotFound({ what }: { what: string }) {
  return (
    <>
      <Topbar title={`${what} not found`} />
      <div className="panel">
        <div className="empty">No records found.</div>
      </div>
    </>
  );
}
